import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowUpRight, Check, ChevronRight, Info } from "lucide-react";
import { api, formatApiError } from "@/lib/auth";
import FileUploader from "@/components/FileUploader";
import { eur } from "@/lib/constants";
import { toast } from "sonner";

const STEPS = ["Support", "Dimensions", "Impression", "Planches", "Fichiers", "Récapitulatif"];
const SUPPORTS = ["Marque-page", "Carte", "Carte postale", "Faire-part / invitation", "Étiquette", "Photo", "Support pédagogique", "Autre"];
const PLATE_TABLE = [
  { n: 1, price: 29 },
  { n: 2, price: 55 },
  { n: 3, price: 79 },
  { n: 4, price: 103 },
  { n: 5, price: 125 },
  { n: 6, price: null, label: "Plus de 5 — demande de devis" },
];
const RV_UNIT = 15;

const initial = {
  support: "",
  support_other: "",
  dim_unknown: false,
  dim_width_mm: "",
  dim_height_mm: "",
  format_planche: "a4",
  format_custom_w: "",
  format_custom_h: "",
  orientation: "auto",
  destination: "",
  has_printer_specs: null,
  printer_specs_text: "",
  printer_specs_file_ids: [],
  trim_marks: "printer",
  plates_count: 1,
  multi_visuals: false,
  visuals_count: "",
  fill_strategy: "auto",
  visuals_breakdown: [],
  plates_rv: [false],
  plate_files: [{ recto: [], verso: [] }],
  file_ids: [],
  notes: "",
  confirm: false,
};

const LABELS = {
  format_planche: { a4: "A4", a3: "A3", custom: "Personnalisé", unknown: "À définir par Charbonier Studio" },
  orientation: { auto: "Automatique", portrait: "Portrait", paysage: "Paysage" },
  destination: { imprimeur: "Imprimeur professionnel", repro: "Reprographe / magasin d'impression", perso: "Imprimante personnelle", unknown: "À définir" },
  trim_marks: { printer: "Selon spécifications imprimeur", yes: "Oui", no: "Non" },
  fill_strategy: { auto: "Optimisation automatique", manual: "Quantités précisées" },
};

function priceFor(state) {
  const n = state.plates_count;
  if (n < 1) return { total: 0, base: 0, rv: 0, surcharge: 0, quote: false };
  if (n > 5) return { total: null, base: null, rv: null, surcharge: null, quote: true };
  const base = PLATE_TABLE.find((p) => p.n === n).price;
  const rv = state.plates_rv.slice(0, n).filter(Boolean).length;
  const surcharge = rv * RV_UNIT;
  return { total: base + surcharge, base, rv, surcharge, quote: false };
}

function StepDots({ step }) {
  return (
    <ol className="flex flex-wrap gap-x-4 gap-y-2 text-xs font-mono">
      {STEPS.map((s, i) => (
        <li key={i} className={`flex items-center gap-2 ${i === step ? "text-[#0E7C90]" : i < step ? "text-black" : "text-neutral-400"}`}>
          {i < step ? <Check size={14} /> : <span className="inline-block w-4 text-center">{i + 1}</span>}
          <span className="uppercase tracking-widest">{s}</span>
          {i < STEPS.length - 1 && <ChevronRight size={12} className="text-neutral-400" />}
        </li>
      ))}
    </ol>
  );
}

function CreationHint() {
  return (
    <div className="mt-6 border border-black/15 bg-white/70 p-4 flex items-start gap-3 text-[13px]">
      <Info size={16} className="mt-0.5 text-[#0E7C90] shrink-0" />
      <div>
        <div className="text-neutral-700">Vos visuels doivent déjà être <span className="font-medium text-black">graphiquement finalisés</span>. Charbonier Studio ne crée pas le design ici.</div>
        <Link to="/creer-mon-support" className="cs-link-underline text-[#0E7C90] mt-1 inline-block">Votre visuel n'existe pas encore ? Faire créer mon support →</Link>
      </div>
    </div>
  );
}

export default function PlancheConfigurator() {
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const [state, setState] = useState(initial);
  const [submitting, setSubmitting] = useState(false);

  const price = useMemo(() => priceFor(state), [state]);
  const set = (k, v) => setState((s) => ({ ...s, [k]: v }));

  // resize plates_rv / plate_files whenever plates_count changes
  useEffect(() => {
    const n = state.plates_count;
    if (n < 1 || n > 5) return;
    setState((s) => {
      const rv = Array.from({ length: n }, (_, i) => s.plates_rv[i] ?? false);
      const files = Array.from({ length: n }, (_, i) => s.plate_files[i] ?? { recto: [], verso: [] });
      return { ...s, plates_rv: rv, plate_files: files };
    });
  }, [state.plates_count]);

  const next = () => setStep((s) => Math.min(s + 1, STEPS.length - 1));
  const prev = () => setStep((s) => Math.max(s - 1, 0));

  const canNext = () => {
    if (step === 0) return !!state.support && (state.support !== "Autre" || state.support_other.trim().length > 0);
    if (step === 1) return state.dim_unknown || (state.dim_width_mm && state.dim_height_mm);
    if (step === 2) return !!state.destination;
    if (step === 3) return state.plates_count >= 1 && state.plates_count <= 5;
    if (step === 4) return true;
    return true;
  };

  const submit = async () => {
    if (state.plates_count > 5) {
      nav("/demande-devis");
      return;
    }
    if (!state.confirm) {
      toast.error("Merci de confirmer votre demande");
      return;
    }
    // Priorité 1 — fichier principal obligatoire pour chaque planche
    for (let i = 0; i < state.plates_count; i++) {
      const files = state.plate_files[i] || { recto: [], verso: [] };
      if (!(files.recto || []).length) {
        toast.error(`Veuillez joindre le fichier recto de la planche ${i + 1} avant de confirmer votre demande.`);
        setStep(4);
        return;
      }
      if (state.plates_rv[i] && !(files.verso || []).length) {
        toast.error(`Veuillez joindre le fichier verso de la planche ${i + 1} avant de confirmer votre demande.`);
        setStep(4);
        return;
      }
    }
    setSubmitting(true);
    try {
      const payload = {
        support: state.support,
        support_other: state.support_other,
        dim_unknown: state.dim_unknown,
        dim_width_mm: state.dim_width_mm ? Number(state.dim_width_mm) : null,
        dim_height_mm: state.dim_height_mm ? Number(state.dim_height_mm) : null,
        format_planche: state.format_planche,
        format_custom_w: state.format_custom_w ? Number(state.format_custom_w) : null,
        format_custom_h: state.format_custom_h ? Number(state.format_custom_h) : null,
        orientation: state.orientation,
        destination: state.destination,
        has_printer_specs: state.has_printer_specs,
        printer_specs_text: state.printer_specs_text,
        printer_specs_file_ids: state.printer_specs_file_ids.map((f) => f.id || f),
        trim_marks: state.trim_marks,
        plates_count: state.plates_count,
        multi_visuals: state.multi_visuals,
        visuals_count: state.visuals_count ? Number(state.visuals_count) : 0,
        fill_strategy: state.fill_strategy,
        visuals_breakdown: state.visuals_breakdown,
        plates_rv: state.plates_rv,
        plate_file_ids: state.plate_files.map((p) => ({
          recto: (p.recto || []).map((f) => f.id),
          verso: (p.verso || []).map((f) => f.id),
        })),
        file_ids: state.file_ids.map((f) => f.id),
        notes: state.notes,
        confirm: state.confirm,
      };
      const { data } = await api.post("/orders/planche", payload);
      toast.success(`Demande enregistrée · ${data.reference}`);
      nav(`/espace/commande/${data.id}`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="cs-cream py-14 min-h-[80vh]" data-testid="page-planche-configurator">
      {/* Mobile running total bar */}
      <div className="lg:hidden sticky top-[73px] z-30 bg-[#FAF8F5]/95 backdrop-blur border-y border-black/10 px-6 py-3 flex items-center justify-between">
        <div className="text-[11px] font-mono uppercase tracking-[0.14em] text-neutral-500">Total en cours</div>
        <div className="font-serif text-2xl" data-testid="planche-total-mobile">{price.total !== null ? eur(price.total) : "Devis"}</div>
      </div>
      <div className="max-w-[1180px] mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-8">
          <div className="cs-eyebrow text-neutral-500">Configurateur · J'ai déjà mon projet</div>
          <h1 className="cs-h1 mt-3 text-4xl lg:text-5xl">Planche PDF prête à imprimer.</h1>
          <p className="mt-3 text-[15px] text-neutral-700 max-w-2xl">
            Configurez votre commande étape par étape. Fonds perdus techniques et traits de coupe sont inclus.
          </p>
          <div className="mt-6"><StepDots step={step} /></div>

          <div className="mt-8 cs-card p-6 sm:p-8" data-testid={`planche-step-${step}`}>
            {step === 0 && (
              <div>
                <h2 className="cs-h3 text-2xl">Que souhaitez-vous mettre en planche ?</h2>
                <div className="mt-6 grid sm:grid-cols-2 gap-3">
                  {SUPPORTS.map((s) => (
                    <button key={s} type="button" onClick={() => set("support", s)}
                      className={`text-left px-4 py-3 border transition-colors ${state.support === s ? "border-[#0E7C90] bg-white" : "border-black/15 bg-white/60 hover:border-black"}`}
                      data-testid={`support-${s}`}>
                      <span className="font-serif text-lg">{s}</span>
                    </button>
                  ))}
                </div>
                {state.support === "Autre" && (
                  <div className="mt-4">
                    <label className="cs-eyebrow text-neutral-500">Précisez votre support</label>
                    <input className="cs-input mt-2" value={state.support_other} onChange={(e) => set("support_other", e.target.value)}
                      placeholder="Ex : signet promotionnel format personnalisé" data-testid="support-other" />
                  </div>
                )}
                <CreationHint />
              </div>
            )}

            {step === 1 && (
              <div>
                <h2 className="cs-h3 text-2xl">Quelles sont les dimensions finales de votre création ?</h2>
                <p className="mt-2 text-sm text-neutral-600">Indiquez les dimensions souhaitées après découpe.</p>
                <div className="mt-6 grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="cs-eyebrow text-neutral-500">Largeur (mm)</label>
                    <input type="number" min="1" className="cs-input mt-2" value={state.dim_width_mm}
                      onChange={(e) => set("dim_width_mm", e.target.value)} disabled={state.dim_unknown} data-testid="dim-w" />
                  </div>
                  <div>
                    <label className="cs-eyebrow text-neutral-500">Hauteur (mm)</label>
                    <input type="number" min="1" className="cs-input mt-2" value={state.dim_height_mm}
                      onChange={(e) => set("dim_height_mm", e.target.value)} disabled={state.dim_unknown} data-testid="dim-h" />
                  </div>
                </div>
                <label className="mt-4 flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={state.dim_unknown} onChange={(e) => set("dim_unknown", e.target.checked)} data-testid="dim-unknown" />
                  <span>Je ne connais pas les dimensions exactes</span>
                </label>

                <div className="mt-8 border-t border-black/10 pt-6">
                  <label className="cs-eyebrow text-neutral-500">Format de la planche</label>
                  <div className="mt-2 grid sm:grid-cols-4 gap-2">
                    {[["a4", "A4"], ["a3", "A3"], ["custom", "Personnalisé"], ["unknown", "Je ne sais pas"]].map(([v, l]) => (
                      <button key={v} type="button" onClick={() => set("format_planche", v)}
                        className={`px-3 py-2.5 border text-sm ${state.format_planche === v ? "border-[#0E7C90] bg-white" : "border-black/15 bg-white/60"}`}
                        data-testid={`format-${v}`}>{l}</button>
                    ))}
                  </div>
                  {state.format_planche === "custom" && (
                    <div className="mt-4 grid sm:grid-cols-2 gap-4">
                      <div>
                        <label className="cs-eyebrow text-neutral-500">Largeur planche (mm)</label>
                        <input type="number" className="cs-input mt-2" value={state.format_custom_w} onChange={(e) => set("format_custom_w", e.target.value)} />
                      </div>
                      <div>
                        <label className="cs-eyebrow text-neutral-500">Hauteur planche (mm)</label>
                        <input type="number" className="cs-input mt-2" value={state.format_custom_h} onChange={(e) => set("format_custom_h", e.target.value)} />
                      </div>
                    </div>
                  )}
                </div>

                <div className="mt-6">
                  <label className="cs-eyebrow text-neutral-500">Orientation</label>
                  <div className="mt-2 grid sm:grid-cols-3 gap-2">
                    {[["auto", "Automatique — recommandé"], ["portrait", "Portrait"], ["paysage", "Paysage"]].map(([v, l]) => (
                      <button key={v} type="button" onClick={() => set("orientation", v)}
                        className={`px-3 py-2.5 border text-sm text-left ${state.orientation === v ? "border-[#0E7C90] bg-white" : "border-black/15 bg-white/60"}`}
                        data-testid={`orient-${v}`}>{l}</button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {step === 2 && (
              <div>
                <h2 className="cs-h3 text-2xl">Comment allez-vous imprimer cette planche ?</h2>
                <div className="mt-6 grid sm:grid-cols-2 gap-3">
                  {[
                    ["imprimeur", "Imprimeur professionnel"],
                    ["repro", "Reprographe / magasin d'impression"],
                    ["perso", "Imprimante personnelle"],
                    ["unknown", "Je ne sais pas encore"],
                  ].map(([v, l]) => (
                    <button key={v} type="button" onClick={() => set("destination", v)}
                      className={`text-left px-4 py-3 border ${state.destination === v ? "border-[#0E7C90] bg-white" : "border-black/15 bg-white/60"}`}
                      data-testid={`dest-${v}`}>
                      <span className="font-serif text-lg">{l}</span>
                    </button>
                  ))}
                </div>

                {state.destination === "imprimeur" && (
                  <div className="mt-6 border-t border-black/10 pt-6">
                    <div className="cs-eyebrow text-neutral-500">Avez-vous reçu des spécifications techniques de votre imprimeur ?</div>
                    <div className="mt-2 flex gap-2">
                      <button type="button" onClick={() => set("has_printer_specs", true)}
                        className={`px-4 py-2 border text-sm ${state.has_printer_specs === true ? "border-[#0E7C90] bg-white" : "border-black/15 bg-white/60"}`}>Oui</button>
                      <button type="button" onClick={() => set("has_printer_specs", false)}
                        className={`px-4 py-2 border text-sm ${state.has_printer_specs === false ? "border-[#0E7C90] bg-white" : "border-black/15 bg-white/60"}`}>Non</button>
                    </div>
                    {state.has_printer_specs && (
                      <div className="mt-4 space-y-3">
                        <textarea rows={4} className="cs-input" placeholder="Collez ou décrivez les indications reçues"
                          value={state.printer_specs_text} onChange={(e) => set("printer_specs_text", e.target.value)} />
                        <FileUploader files={state.printer_specs_file_ids} setFiles={(fn) => set("printer_specs_file_ids", typeof fn === "function" ? fn(state.printer_specs_file_ids) : fn)}
                          testId="printer-specs-upload" category="printer_specs" />
                      </div>
                    )}
                  </div>
                )}

                <div className="mt-8 border-t border-black/10 pt-6">
                  <div className="cs-eyebrow text-neutral-500">Fonds perdus</div>
                  <p className="mt-2 text-[14px] text-neutral-700">
                    Charbonier Studio ajoute les fonds perdus nécessaires à l'impression lorsque le fichier le permet.
                    <span className="text-black"> Cette intervention est incluse dans votre prestation.</span>
                  </p>
                  <p className="mt-2 text-[13px] text-neutral-600">
                    Si la création des fonds perdus nécessite de reconstruire graphiquement une partie importante de votre visuel, une estimation complémentaire pourra être proposée avant toute intervention.
                    <span className="text-black"> Aucun supplément sans votre accord.</span>
                  </p>
                </div>

                <div className="mt-6 border-t border-black/10 pt-6">
                  <label className="cs-eyebrow text-neutral-500">Traits de coupe</label>
                  <div className="mt-2 grid sm:grid-cols-3 gap-2">
                    {[["printer", "Selon les recommandations de l'imprimeur"], ["yes", "Oui"], ["no", "Non"]].map(([v, l]) => (
                      <button key={v} type="button" onClick={() => set("trim_marks", v)}
                        className={`px-3 py-2.5 border text-sm text-left ${state.trim_marks === v ? "border-[#0E7C90] bg-white" : "border-black/15 bg-white/60"}`}
                        data-testid={`trim-${v}`}>{l}</button>
                    ))}
                  </div>
                  <p className="mt-2 text-[12px] font-mono text-neutral-500">Inclus · aucun supplément.</p>
                </div>
              </div>
            )}

            {step === 3 && (
              <div>
                <h2 className="cs-h3 text-2xl">Combien de planches différentes ?</h2>
                <p className="mt-2 text-sm text-neutral-600">Une « planche différente » = une nouvelle composition. Une duplication à l'identique n'est jamais facturée.</p>
                <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {PLATE_TABLE.map((p) => (
                    <button key={p.n} type="button" onClick={() => set("plates_count", p.n)}
                      className={`text-left p-4 border ${state.plates_count === p.n ? "border-[#0E7C90] bg-white" : "border-black/15 bg-white/60"}`}
                      data-testid={`plates-${p.n}`}>
                      <div className="font-serif text-2xl">{p.n === 6 ? "6+" : `${p.n} planche${p.n > 1 ? "s" : ""}`}</div>
                      <div className="font-mono text-sm mt-1">{p.price !== null ? `${p.price} €` : "Devis"}</div>
                    </button>
                  ))}
                </div>
                <p className="mt-4 text-[13px] text-neutral-600">Vous pourrez imprimer chaque PDF autant de fois que vous le souhaitez. Une duplication strictement identique n'est jamais facturée.</p>

                {state.plates_count <= 5 && (
                  <>
                    <div className="mt-8 border-t border-black/10 pt-6">
                      <div className="cs-eyebrow text-neutral-500">Plusieurs modèles sur la même planche ?</div>
                      <div className="mt-2 flex gap-2">
                        <button type="button" onClick={() => set("multi_visuals", true)}
                          className={`px-4 py-2 border text-sm ${state.multi_visuals ? "border-[#0E7C90] bg-white" : "border-black/15 bg-white/60"}`}
                          data-testid="multi-yes">Oui</button>
                        <button type="button" onClick={() => set("multi_visuals", false)}
                          className={`px-4 py-2 border text-sm ${!state.multi_visuals ? "border-[#0E7C90] bg-white" : "border-black/15 bg-white/60"}`}
                          data-testid="multi-no">Non</button>
                      </div>
                      {state.multi_visuals && (
                        <div className="mt-4 space-y-3">
                          <div>
                            <label className="cs-eyebrow text-neutral-500">Combien de visuels différents ?</label>
                            <input type="number" min="2" className="cs-input mt-2 max-w-[180px]" value={state.visuals_count}
                              onChange={(e) => set("visuals_count", e.target.value)} data-testid="visuals-count" />
                          </div>
                          <div>
                            <label className="cs-eyebrow text-neutral-500">Comment souhaitez-vous les répartir ?</label>
                            <div className="mt-2 grid sm:grid-cols-2 gap-2">
                              {[["auto", "Optimiser automatiquement — recommandé"], ["manual", "Je précise les quantités"]].map(([v, l]) => (
                                <button key={v} type="button" onClick={() => set("fill_strategy", v)}
                                  className={`px-3 py-2 border text-sm text-left ${state.fill_strategy === v ? "border-[#0E7C90] bg-white" : "border-black/15 bg-white/60"}`}
                                  data-testid={`fill-${v}`}>{l}</button>
                              ))}
                            </div>
                          </div>
                          {state.fill_strategy === "manual" && state.visuals_count > 0 && (
                            <div className="space-y-2">
                              {Array.from({ length: Number(state.visuals_count) }).map((_, i) => {
                                const row = state.visuals_breakdown[i] || { label: String.fromCharCode(65 + i), quantity: "" };
                                return (
                                  <div key={i} className="grid grid-cols-[80px_1fr_140px] gap-2 items-center">
                                    <span className="font-mono text-sm">Modèle {row.label}</span>
                                    <input className="cs-input" placeholder="Nom / référence" value={row.label} onChange={(e) => {
                                      const arr = [...state.visuals_breakdown];
                                      arr[i] = { ...(arr[i] || row), label: e.target.value };
                                      set("visuals_breakdown", arr);
                                    }} />
                                    <input type="number" min="1" className="cs-input" placeholder="Exemplaires" value={row.quantity} onChange={(e) => {
                                      const arr = [...state.visuals_breakdown];
                                      arr[i] = { ...(arr[i] || row), quantity: Number(e.target.value) || 0 };
                                      set("visuals_breakdown", arr);
                                    }} />
                                  </div>
                                );
                              })}
                              <p className="text-[12px] font-mono text-neutral-500">Les volumes exceptionnellement importants peuvent nécessiter une estimation complémentaire.</p>
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    <div className="mt-8 border-t border-black/10 pt-6">
                      <div className="cs-eyebrow text-neutral-500">Recto-verso par planche</div>
                      <p className="mt-2 text-[13px] text-neutral-600">Chaque planche recto-verso ajoute +15 €.</p>
                      <div className="mt-3 space-y-2">
                        {Array.from({ length: state.plates_count }).map((_, i) => (
                          <div key={i} className="flex items-center justify-between border border-black/10 bg-white px-4 py-3">
                            <span className="font-serif text-lg">Planche {i + 1}</span>
                            <div className="flex gap-2">
                              <button type="button" onClick={() => {
                                const arr = [...state.plates_rv]; arr[i] = false; set("plates_rv", arr);
                              }} className={`px-3 py-1.5 text-sm border ${!state.plates_rv[i] ? "border-[#0E7C90] bg-[#F5F1EC]" : "border-black/15"}`}
                                data-testid={`rv-${i}-no`}>Recto</button>
                              <button type="button" onClick={() => {
                                const arr = [...state.plates_rv]; arr[i] = true; set("plates_rv", arr);
                              }} className={`px-3 py-1.5 text-sm border ${state.plates_rv[i] ? "border-[#0E7C90] bg-[#F5F1EC]" : "border-black/15"}`}
                                data-testid={`rv-${i}-yes`}>Recto-verso · +15 €</button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </>
                )}

                {state.plates_count > 5 && (
                  <div className="mt-8 border border-black/15 bg-white p-6">
                    <div className="cs-eyebrow text-[#0E7C90]">Projet important</div>
                    <p className="mt-3 text-[15px] text-neutral-700">
                      Nous vous proposons un tarif personnalisé à partir de votre projet. Les commandes importantes bénéficient d'une tarification dégressive.
                    </p>
                    <Link to="/demande-devis" className="cs-btn-primary mt-6 inline-flex" data-testid="planche-quote-cta">
                      Demander mon estimation <ArrowUpRight size={16} />
                    </Link>
                  </div>
                )}
              </div>
            )}

            {step === 4 && (
              <div>
                <h2 className="cs-h3 text-2xl">Envoyez vos créations</h2>
                <p className="mt-2 text-sm text-neutral-600">Formats acceptés : PDF, JPG, PNG, TIFF, PSD, ZIP. Vos créations doivent être graphiquement finalisées.</p>

                {state.plates_count >= 1 && state.plates_count <= 5 && (
                  <div className="mt-6 space-y-6">
                    {Array.from({ length: state.plates_count }).map((_, i) => (
                      <div key={i} className="border border-black/15 bg-white p-4">
                        <div className="flex items-center justify-between">
                          <div className="font-serif text-lg">Planche {i + 1}</div>
                          <div className="font-mono text-xs text-neutral-500">{state.plates_rv[i] ? "Recto-verso" : "Recto seul"}</div>
                        </div>
                        <div className="mt-3">
                          <div className="cs-eyebrow text-neutral-500">Fichier(s) recto</div>
                          <div className="mt-2">
                            <FileUploader files={state.plate_files[i]?.recto || []}
                              setFiles={(fn) => {
                                const cur = state.plate_files[i]?.recto || [];
                                const nxt = typeof fn === "function" ? fn(cur) : fn;
                                const arr = [...state.plate_files];
                                arr[i] = { ...(arr[i] || { recto: [], verso: [] }), recto: nxt };
                                set("plate_files", arr);
                              }}
                              testId={`plate-${i}-recto`} category={`plate_${i + 1}_recto`} />
                          </div>
                        </div>
                        {state.plates_rv[i] && (
                          <div className="mt-4">
                            <div className="cs-eyebrow text-neutral-500">Fichier(s) verso associé</div>
                            <div className="mt-2">
                              <FileUploader files={state.plate_files[i]?.verso || []}
                                setFiles={(fn) => {
                                  const cur = state.plate_files[i]?.verso || [];
                                  const nxt = typeof fn === "function" ? fn(cur) : fn;
                                  const arr = [...state.plate_files];
                                  arr[i] = { ...(arr[i] || { recto: [], verso: [] }), verso: nxt };
                                  set("plate_files", arr);
                                }}
                                testId={`plate-${i}-verso`} category={`plate_${i + 1}_verso`} />
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                <div className="mt-8 border-t border-black/10 pt-6">
                  <label className="cs-eyebrow text-neutral-500">Une consigne particulière ? (facultatif)</label>
                  <textarea rows={4} className="cs-input mt-2" value={state.notes} onChange={(e) => set("notes", e.target.value)}
                    placeholder="Ex : ordre des modèles, marges, disposition souhaitée…" data-testid="planche-notes" />
                </div>

                <div className="mt-4 border border-black/15 bg-white/70 p-4 text-[13px] text-neutral-700">
                  Le tarif comprend la mise en planche et la préparation technique. Il ne comprend pas la création graphique,
                  modification importante du design, retouche complexe ou remplacement d'éléments. Le cas échéant, une estimation vous sera proposée <span className="text-black">avant tout travail supplémentaire</span>.
                </div>
              </div>
            )}

            {step === 5 && (
              <div>
                <h2 className="cs-h3 text-2xl">Votre planche PDF</h2>
                <dl className="mt-6 grid sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
                  <Row k="Support" v={state.support === "Autre" ? `Autre — ${state.support_other}` : state.support} />
                  <Row k="Dimensions finales" v={state.dim_unknown ? "à préciser" : `${state.dim_width_mm || "—"} × ${state.dim_height_mm || "—"} mm`} />
                  <Row k="Format planche" v={state.format_planche === "custom" ? `${state.format_custom_w}×${state.format_custom_h} mm` : LABELS.format_planche[state.format_planche]} />
                  <Row k="Orientation" v={LABELS.orientation[state.orientation]} />
                  <Row k="Destination" v={LABELS.destination[state.destination]} />
                  <Row k="Nb planches différentes" v={String(state.plates_count)} />
                  <Row k="Recto-verso" v={`${price.rv || 0} planche(s)`} />
                  <Row k="Fonds perdus" v="Inclus" />
                  <Row k="Traits de coupe" v={LABELS.trim_marks[state.trim_marks]} />
                  <Row k="Multi-visuels" v={state.multi_visuals ? `${state.visuals_count || "?"} — ${LABELS.fill_strategy[state.fill_strategy]}` : "Non"} />
                </dl>

                <div className="mt-6 border-t border-black/10 pt-4 flex items-end justify-between">
                  <div>
                    <div className="cs-eyebrow text-neutral-500">Prix</div>
                    <div className="font-serif text-4xl">{price.total !== null ? eur(price.total) : "Sur devis"}</div>
                    <div className="text-[12px] font-mono text-neutral-500 mt-1">Base {price.base ? eur(price.base) : "—"} · Recto-verso {price.surcharge ? `+${eur(price.surcharge)}` : "0 €"}</div>
                  </div>
                </div>

                <div className="mt-8">
                  <div className="cs-eyebrow text-neutral-500">Ce qui est inclus</div>
                  <ul className="mt-3 grid sm:grid-cols-2 gap-2 text-[14px]">
                    {[
                      "Création de la ou des planches",
                      "Placement des visuels",
                      "Fonds perdus techniques nécessaires",
                      "Traits de coupe lorsque nécessaires",
                      "Préparation recto-verso si commandée",
                      "Contrôle technique",
                      "PDF HD prêt à imprimer",
                      "1 ajustement technique mineur",
                    ].map((it) => (
                      <li key={it} className="flex items-start gap-2"><Check size={16} className="mt-0.5 text-[#0E7C90] shrink-0" /><span>{it}</span></li>
                    ))}
                  </ul>
                  <p className="mt-4 text-[13px] font-mono text-neutral-600">Aucun supplément sans votre accord.</p>
                </div>

                <label className="mt-8 flex items-start gap-2 text-[14px]" data-testid="planche-confirm-label">
                  <input type="checkbox" checked={state.confirm} onChange={(e) => set("confirm", e.target.checked)} data-testid="planche-confirm" />
                  <span>Je confirme que les dimensions, fichiers et informations indiqués dans ce récapitulatif correspondent à ma demande.</span>
                </label>

                {/* Priorité 1 — bloc d'alerte tant qu'aucun fichier n'a été joint */}
                {(() => {
                  const missing = [];
                  for (let i = 0; i < state.plates_count; i++) {
                    const f = state.plate_files[i] || { recto: [], verso: [] };
                    if (!(f.recto || []).length) missing.push(`Planche ${i + 1} — recto`);
                    if (state.plates_rv[i] && !(f.verso || []).length) missing.push(`Planche ${i + 1} — verso`);
                  }
                  if (!missing.length) return null;
                  return (
                    <div role="alert" aria-live="polite" className="mt-4 text-[13px] text-red-700 border border-red-400 bg-red-50 px-3 py-2" data-testid="planche-file-missing">
                      Veuillez joindre le(s) fichier(s) suivant(s) avant de confirmer votre demande : {missing.join(", ")}.
                    </div>
                  );
                })()}

                <button onClick={submit} disabled={!state.confirm || submitting || (() => {
                  for (let i = 0; i < state.plates_count; i++) {
                    const f = state.plate_files[i] || { recto: [], verso: [] };
                    if (!(f.recto || []).length) return true;
                    if (state.plates_rv[i] && !(f.verso || []).length) return true;
                  }
                  return false;
                })()} className="cs-btn-primary mt-6 w-full sm:w-auto justify-center disabled:opacity-50" data-testid="planche-submit">
                  {submitting ? "Envoi…" : "Valider ma demande"} <ArrowUpRight size={16} />
                </button>
              </div>
            )}
          </div>

          {step < 5 && (
            <div className="mt-6 flex items-center justify-between">
              <button onClick={prev} disabled={step === 0} className="text-sm cs-link-underline disabled:opacity-40" data-testid="planche-prev">← Retour</button>
              <button onClick={next} disabled={!canNext()} className="cs-btn-primary disabled:opacity-50" data-testid="planche-next">Continuer <ArrowUpRight size={18} /></button>
            </div>
          )}
        </div>

        <aside className="lg:col-span-4">
          <div className="cs-card p-6 sticky top-24" data-testid="planche-price-panel">
            <div className="cs-eyebrow text-neutral-500">Récapitulatif</div>
            <div className="mt-3 font-serif text-2xl">Planche PDF prête à imprimer</div>

            <div className="mt-4 border-t border-black/10 pt-3">
              <div className="flex justify-between text-sm">
                <span>{state.plates_count} planche{state.plates_count > 1 ? "s" : ""} différente{state.plates_count > 1 ? "s" : ""}</span>
                <span className="font-mono">{price.base !== null ? eur(price.base) : "—"}</span>
              </div>
              {price.rv > 0 && (
                <div className="flex justify-between text-sm mt-1">
                  <span>Recto-verso × {price.rv}</span>
                  <span className="font-mono">+ {eur(price.surcharge)}</span>
                </div>
              )}
            </div>

            <div className="mt-4 border-t border-black/10 pt-3 flex justify-between items-baseline">
              <span className="cs-eyebrow text-neutral-500">Total</span>
              <span className="font-serif text-3xl" data-testid="planche-total">{price.total !== null ? eur(price.total) : "Devis"}</span>
            </div>

            <ul className="mt-4 text-[12px] font-mono text-neutral-500 space-y-1">
              <li>Fonds perdus inclus</li>
              <li>Traits de coupe inclus</li>
              <li>PDF HD final inclus</li>
              <li>Duplication identique gratuite</li>
            </ul>
          </div>
        </aside>
      </div>
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex justify-between border-b border-black/10 pb-2">
      <dt className="text-neutral-500 font-mono text-xs uppercase tracking-widest">{k}</dt>
      <dd className="text-right">{v || "—"}</dd>
    </div>
  );
}
